import { message } from "antd";
import { useCallback, useEffect, useRef } from "react";
import {
  FieldValues,
  SubmitErrorHandler,
  useForm,
  UseFormProps,
  UseFormReturn,
} from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { setEtudeId } from "store/etudeSlice";
import { generateUniqueId } from "utils/helpers";
import { ValidationError } from "../errors/ValidationError";
import { getDefaultNetwork } from "utils/defines";



const getNetworkFromUrl = (networkCode: string) => {
  let _network = getDefaultNetwork();
  switch (networkCode) {
    case "ATLFR":
      _network = "ATLFR";
      break;
    case "ACVFR":
      _network = "ACVFR";
      break;
    case "YGNBE":
      _network = "YGNBE";
      break;
    case "YGNIT":
      _network = "YGNIT";
      break;
    case "ACVES":
      _network = "ACVES";
      break;
    case "ACVUK":
      _network = "ACVUK";
      break;
    case "HAMUK":
      _network = "HAMUK";
      break;
    case "ACVGABe":
      _network = "ACVGABe";
      break;
    case "IDEAL":
      _network = "IDEAL";
      break;
    case "ACVPL":
      _network = "ACVPL";
      break;
    case "ACVDE":
      _network = "ACVDE";
      break;
    case "ACVCZ":
      _network = "ACVCZ";
      break;
    case "ACVIT":
      _network = "ACVIT";
      break;
    case "ATLPOL":
      _network = "ATLPOL";
      break;
  }

  return _network;
}

export type UseCustomFormReturn<Fields extends FieldValues> = Omit<
  UseFormReturn<Fields>,
  "handleSubmit"
> & {
  handleSubmit: HandleSubmit<Fields>;
};

export type HandleSubmit<TFieldValues extends FieldValues> = (
  onValid: (values: TFieldValues) => Promise<unknown> | void,
  onSuccess?: () => void,
  onInvalid?: SubmitErrorHandler<TFieldValues>
) => (e?: React.BaseSyntheticEvent) => Promise<void>;

const useCustomForm = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = any
>(
  props?: UseFormProps<TFieldValues, TContext> & {
    preserveData?: boolean;
    preservationKey?: string;
  }
): UseCustomFormReturn<TFieldValues> => {
  const useFormResult = useForm<TFieldValues>(props);
  // const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const preservationKey = useRef(props?.preservationKey || "");
  const params = useParams();

  const preservationTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const { etudeId } = useParams();

  useEffect(() => {
    if (props?.preserveData) {
      if (!preservationKey.current) {
        preservationKey.current =
          etudeId && etudeId !== ":etudeId"
            ? etudeId
            : "tmp_" + generateUniqueId();
        // searchParams.get("formPreservationKey") ||;

        dispatch(setEtudeId(preservationKey.current));
      }

      useFormResult.watch((values) => {
        if (preservationTimeout.current) {
          clearTimeout(preservationTimeout.current);
        }

        preservationTimeout.current = setTimeout(() => {
          sessionStorage.setItem(
            preservationKey.current,
            JSON.stringify(values)
          );
        }, 1000);
      });

      if (etudeId && etudeId !== ":etudeId") {
        const sData = sessionStorage.getItem(preservationKey.current);

        if (sData) {
          useFormResult.reset(JSON.parse(sData));
        }
      } else if (etudeId === ":etudeId") {
        const _network = getNetworkFromUrl(params.network as string);
        const path = `/${_network}/etude/${params.etudeType}/${preservationKey.current}`;
        navigate(path, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.preserveData, useFormResult]);

  const handleSubmit: HandleSubmit<TFieldValues> = useCallback(
    (onValid, onSuccess, onInvalid) => {
      const onValidOverridden = (values: TFieldValues) => {
        const onValidRes = onValid(values);
        if (!onValidRes) {
          return;
        }
        return onValidRes
          .then((displayMessage = true) => {
            if (displayMessage) message.success("Success");
          })
          .then(onSuccess)
          .catch(
            (error: {
              message?: string;
              data: ValidationError;
              status: string | number;
            }) => {
              const err = error.data;
              let messageTexts = [error.message ?? err?.title ?? ""];
              if (err?.errors) {
                for (const field in err.errors) {
                  useFormResult.setError(field as any, {
                    message: err.errors[field],
                  });
                  messageTexts.push(err.errors[field]);
                }
              }
              message.error(
                <group data-direction="column" data-align="left">
                  {messageTexts.map((text) => (
                    <group>{text}</group>
                  ))}
                </group>
              );

              // throw err;
            }
          );
      };

      return useFormResult.handleSubmit(onValidOverridden, onInvalid);
    },
    [useFormResult]
  );

  return { ...useFormResult, handleSubmit };
};

export default useCustomForm;
